import { CSSProperties, useEffect, useRef } from 'react';

// A 1x1 transparent GIF. Android's WebView draws its own default poster, a
// play symbol, over any <video> without one until the first frame arrives;
// giving the video this poster replaces it with nothing.
const TRANSPARENT_POSTER = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

export interface AmbientVideoProps {
  src: string;
  className?: string;
  style?: CSSProperties;
}

/**
 * AmbientVideo — a silent, looping, decorative video (Alma's persona clip).
 * Never shows playback UI. Bundled animations (hourglass, falling coins) are
 * animated WebP images instead, which iOS never gates behind autoplay.
 *
 * iOS draws a native play button over a <video> whenever it won't autoplay it
 * — in Low Power Mode, or for a moment while it loads. So:
 *   - the `.sk-ambient-video` rule in index.css hides WebKit's media controls,
 *     including that start-playback button;
 *   - `muted` is set on the element itself: React only sets the property, and
 *     iOS decides whether a video may autoplay from the attribute;
 *   - if autoplay is refused, the first tap anywhere (a gesture iOS accepts)
 *     or returning to the app starts it instead. Until then the first frame
 *     shows as a still image.
 * Android's WebView has its own version of the same problem: with no poster it
 * puts a play symbol over the video until the first frame arrives, which the
 * rule above can't hide. The transparent `poster` below replaces it.
 * Content videos people choose to watch (host intros) keep normal controls.
 */
export function AmbientVideo({ src, className, style }: AmbientVideoProps) {
  const ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');
    const tryPlay = () => {
      if (video.paused && document.visibilityState !== 'hidden') void video.play()?.catch(() => {});
    };
    tryPlay();
    document.addEventListener('pointerdown', tryPlay, { passive: true });
    document.addEventListener('visibilitychange', tryPlay);
    return () => {
      document.removeEventListener('pointerdown', tryPlay);
      document.removeEventListener('visibilitychange', tryPlay);
    };
  }, [src]);

  return (
    <video
      ref={ref}
      src={src}
      poster={TRANSPARENT_POSTER}
      autoPlay
      loop
      muted
      playsInline
      preload="auto"
      disablePictureInPicture
      disableRemotePlayback
      aria-hidden
      tabIndex={-1}
      className={className ? `sk-ambient-video ${className}` : 'sk-ambient-video'}
      style={{ pointerEvents: 'none', ...style }}
    />
  );
}
